import React, { useEffect, useState } from "react";
import { DashboardReportsApi } from "api/DashboardReportsApi";
import { useDeleteReportGeneration, useGetGeneratedReportData } from "api/api-hooks/reports-hooks";
import { GeneratedDashboardReportResponse } from "api/model/dashboard/GeneratedDashboardReportResponse";
import { ReportStatus, ReportType } from "api/model/enums";
import { T } from "services/lang/LanguageService";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnFilterApplyClickParams } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "react-tooltip";
import { TemplateLoadingType } from "utils/enums";
import { addToDate, formatDateTimeOrNow, formatTime } from "utils/time-utils";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import { IPeriodLoadingConfig } from "../product-template-table/utils/interfaces";
import { getTemplateLoadingRange } from "../product-template-table/utils/timeUtils";
import GenerateNewReport from "./GenerateNewReport";
import GeneratedReportHistoryList from "./GeneratedReportHistoryList";
import MarkForRegeneration from "./MarkForRegeneration";
import RemoveReportRange from "./RemoveReportRange";
import { IComponentTimed } from "../../pages/dashboard/components/interfaces";

export type GeneratedReportTableProps = {
  ids: Array<number>;
  type?: ReportType;
  loadingConfig?: IPeriodLoadingConfig;
  itemsPerPage?: number;
  sort?: "ASC" | "DESC";
  referenceTimePattern?: string;
  status?: ReportStatus;
  showDateSelection?: boolean;
  hideHeader?: boolean;

  superUserComponent?: boolean;
} & IComponentTimed;

type FilterType = {
  dashboardId?: Array<number>;
  onlyType?: ReportType;
};

const statusMap: Record<ReportStatus, any> = {
  SUCCESS: (
    <i
      data-tooltip-content={T("alert.success")}
      style={{ color: "green" }}
      className="pi pi-check status-body-tooltip"
    />
  ),
  FAILED: (
    <i
      data-tooltip-content={T("alert.failed")}
      style={{ color: "red" }}
      className="pi pi-times status-body-tooltip"
    />
  ),
  FAILED_RETRYING: (
    <i
      data-tooltip-content={T("alert.failed-retrying")}
      style={{ color: "orange" }}
      className="pi pi-exclamation-triangle status-body-tooltip"
    />
  ),
  GENERATING: (
    <i
      data-tooltip-content={T("alert.generating")}
      className="pi pi-spin pi-spinner status-body-tooltip"
    />
  ),
  MARKED_FOR_REGENERATION: (
    <i
      data-tooltip-content={T("alert.marked-for-regeneration")}
      style={{ color: "red" }}
      className="pi pi-spin pi-hourglass status-body-tooltip"
    />
  ),
  QUEUED: (
    <i
      data-tooltip-content={T("alert.queued")}
      style={{ color: "orange" }}
      className="pi pi-spin pi-hourglass status-body-tooltip"
    />
  ),
};

const typeMap: Record<ReportType, any> = {
  MANUAL: (
    <i
      data-tooltip-content={T("word.report-table.manual")}
      className="pi pi-user type-body-tooltip"
    />
  ),
  SCHEDULED: (
    <i
      data-tooltip-content={T("word.report-table.scheduled")}
      className="pi pi-server type-body-tooltip"
    />
  ),
};

const GeneratedReportTable = (props: GeneratedReportTableProps) => {
  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [dateChanged, setDateChanged] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [removeDialog, setRemoveDialog] = useState(false);
  const [filterInfo, setFilerInfo] = useState<FilterType>({});
  const [generateDialog, setGenerateDialog] = useState(false);
  const [removeRangeDialog, setRemoveRangeDialog] = useState(false);
  const [dialogFunction, setDialogFunction] = useState<"REMOVE" | "CLEANUP">("REMOVE");

  const rowsPerPageOptions = props.itemsPerPage
    ? [props.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter((x, i, a) => {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const { data, isLoading, isError, refetch } = useGetGeneratedReportData(
    {
      id: props.ids,
      onlyType: props.type,
      from: from?.toISOString(),
      to: to?.toISOString(),
      sort: props.sort,
      status: props.status || [
        ReportStatus.GENERATING,
        ReportStatus.FAILED,
        ReportStatus.FAILED_RETRYING,
        ReportStatus.SUCCESS,
        ReportStatus.QUEUED,
        ReportStatus.MARKED_FOR_REGENERATION,
      ],
    },
    {
      enabled: !!from && !!to,
    }
  );

  const { mutate: deleteReportGeneration } = useDeleteReportGeneration({
    onSuccess: () => {
      easasAlert({
        summary: T("alert.report-table.report-generation-deleted"),
        severity: "success",
      });
    },
    onFailure: () => {
      easasAlert({
        summary: T("alert.report-table.failed-to-delete-report-generation"),
        severity: "error",
      });
    },
  });

  useEffect(() => {
    setDatesByConfig();
  }, []);

  const setDatesByConfig = () => {
    if (props.loadingConfig) {
      const { start, end } = getTemplateLoadingRange(
        {
          loadingType: TemplateLoadingType.RANGE,
          loadingConfig: props.loadingConfig,
        },
        props.referenceTime
      );

      setFrom(start);
      setTo(end);
    } else {
      setFrom(addToDate(new Date(), -1, "DAYS"));
      setTo(new Date());
    }
    setDateChanged(false);
  };

  const filterDashBoardId = (e: ColumnFilterApplyClickParams) => {
    // @ts-ignore
    const keyPart = e.constraints.constraints[0].value;

    if (keyPart) {
      const arr: Array<GeneratedDashboardReportResponse> =
        data as Array<GeneratedDashboardReportResponse>;

      const ar = arr.reduce((acc, ob) => {
        if (ob.dashboardTitle.includes(keyPart) && ob.dashboardId) {
          acc.push(ob.dashboardId as number);
        }
        return acc;
      }, [] as number[]);

      const uniqueArr: Array<number> = [];
      for (let i = 0; i < ar.length; i++) {
        if (uniqueArr.indexOf(ar[i]) === -1) {
          uniqueArr.push(ar[i]);
        }
      }

      setFilerInfo(prev => ({ ...prev, dashboardId: uniqueArr }));
    } else {
      setFilerInfo(prev => ({ ...prev, dashboardId: undefined }));
    }
  };

  const onZipDownloadClick = () => {
    let dashboardIds = props.ids;
    let onlyType = props.type;

    if (filterInfo) {
      if (filterInfo.dashboardId) {
        if (filterInfo.dashboardId.length === 0) {
          console.warn("No ids was filtered");
        } else {
          dashboardIds = filterInfo.dashboardId;
        }
      }

      if (filterInfo.onlyType) {
        onlyType = filterInfo.onlyType;
      }
    }

    if (dashboardIds.length > 0 && from) {
      window.open(
        DashboardReportsApi.getDownloadZipUrl(
          dashboardIds,
          from.toISOString(),
          to?.toISOString(),
          onlyType
        )
      );
    }
  };

  const reportTypesCheckboxFiltering = (options: any) => {
    return (
      <div>
        <div className="field-radiobutton">
          <RadioButton
            inputId="type"
            value={ReportType.SCHEDULED}
            name="types"
            onChange={e => {
              setFilerInfo(val => ({ ...val, onlyType: e.value }));
              options.filterCallback(e.value);
            }}
            checked={options.value === ReportType.SCHEDULED}
          />
          <label htmlFor="type">{T("word.report-table.scheduled")}</label>
        </div>
        <div className="field-radiobutton" style={{ marginBottom: 0 }}>
          <RadioButton
            inputId="type2"
            value={ReportType.MANUAL}
            name="types"
            onChange={e => {
              setFilerInfo(val => ({ ...val, onlyType: e.value }));
              options.filterCallback(e.value);
            }}
            checked={options.value === ReportType.MANUAL}
          />
          <label htmlFor="type2">{T("word.report-table.manual")}</label>
        </div>
      </div>
    );
  };

  const reportHistory = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <>
        <Dialog
          header={T("word.report-table.report-history")}
          visible={historyDialog}
          style={{ minWidth: "10vw" }}
          onHide={() => setHistoryDialog(false)}
        >
          <GeneratedReportHistoryList
            data={rowData.fileIds.map(e => ({ file: e }))}
            genId={rowData.id}
            closeDialog={() => setHistoryDialog(false)}
          />
        </Dialog>
        <div className="flex align-items-center">
          {rowData.fileIds && rowData.fileIds.length > 1 && (
            <Button
              icon="pi pi-list"
              className="p-button-rounded p-button-text p-button-sm"
              onClick={() => {
                setHistoryDialog(true);
              }}
            />
          )}
        </div>
      </>
    );
  };

  const downloadReportBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      (
        <div className="flex align-items-center">
          {rowData.fileIds && rowData.fileIds.length > 0 && (
            <Button
              icon="pi pi-download"
              className="p-button-rounded p-button-text p-button-sm"
              onClick={() => {
                window.open(
                  DashboardReportsApi.getDownloadUrl(
                    rowData.id,
                    rowData.fileIds[rowData.fileIds.length - 1]
                  )
                );
              }}
            />
          )}
        </div>
      ) || null
    );
  };

  const deleteGenerationBody = (rowData: GeneratedDashboardReportResponse) => {
    const accept = () => {
      deleteReportGeneration({ genId: rowData.id });
    };

    return (
      <>
        <ConfirmDialog
          visible={removeDialog}
          onHide={() => setRemoveDialog(false)}
          message={T("confirm-dialog.report-table.do-you-want-to-remove-this-report")}
          header={T("confirm-dialog.report-table.delete-confirmation")}
          icon="pi pi-info-circle"
          acceptClassName="p-button-danger"
          accept={accept}
        />
        <div className="flex align-items-center">
          <Button
            icon="pi pi-trash"
            style={{ color: "red" }}
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => {
              setRemoveDialog(true);
            }}
          />
        </div>
      </>
    );
  };

  const typeBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <>
        <Tooltip anchorSelect=".type-body-tooltip" delayShow={200} />
        {typeMap[rowData.type]}
      </>
    );
  };

  const statusBody = (rowData: GeneratedDashboardReportResponse) => {
    return (
      <>
        <Tooltip anchorSelect=".status-body-tooltip" delayShow={200} />
        {statusMap[rowData.status]}
      </>
    );
  };

  if (isError) {
    return <div>{T("error.report-table.component-error")}</div>;
  }

  return (
    <>
      <div className="flex flex-column gap-2">
        {!props.hideHeader && (
          <div className="flex gap-2 align-items-end justify-content-between">
            <div className="flex gap-2 align-items-end">
              {props.showDateSelection && (
                <div className="flex gap-2">
                  <div className="flex flex-column gap-1">
                    <span style={{ fontWeight: "bold" }}>{T("word.from")}:</span>
                    <CalendarWrapper
                      value={from as Date}
                      onChange={e => {
                        setDateChanged(true);
                        setFrom(e);
                      }}
                    />
                  </div>
                  <div className="flex flex-column gap-1">
                    <span style={{ fontWeight: "bold" }}>{T("word.to")}:</span>
                    <CalendarWrapper
                      value={to as Date}
                      onChange={e => {
                        setDateChanged(true);
                        setTo(e);
                      }}
                    />
                  </div>
                </div>
              )}

              {dateChanged && (
                <div>
                  <Button icon="pi pi-refresh" onClick={() => setDatesByConfig()} />
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <div>
                <Button
                  icon="pi pi-refresh"
                  className="p-button-sm"
                  onClick={() => {
                    refetch().then(() =>
                      easasAlert({
                        summary: T("alert.report-table.data-reloaded-successfully"),
                        severity: "success",
                        life: 3000,
                      })
                    );
                  }}
                  disabled={!props.ids || props.ids.length === 0}
                />
              </div>
              <div>
                <Button
                  icon="pi pi-cog"
                  className="p-button-sm"
                  label={T("action.generate")}
                  onClick={() => setGenerateDialog(true)}
                  disabled={!props.ids || props.ids.length === 0}
                />
              </div>

              {props.superUserComponent && (
                <div>
                  <Button
                    icon="pi pi-history"
                    className="p-button-sm p-button-danger"
                    label={T("action.cleanup")}
                    onClick={() => {
                      setDialogFunction("CLEANUP");
                      setRemoveRangeDialog(true);
                    }}
                    disabled={!props.ids || props.ids.length === 0}
                  />
                </div>
              )}

              {props.superUserComponent && (
                <div>
                  <Button
                    icon="pi pi-trash"
                    className="p-button-sm p-button-danger"
                    label={T("word.reports")}
                    onClick={() => {
                      setDialogFunction("REMOVE");
                      setRemoveRangeDialog(true);
                    }}
                    disabled={!props.ids || props.ids.length === 0}
                  />
                </div>
              )}
              <div>
                <Button
                  icon="pi pi-folder"
                  className="p-button-sm"
                  label={T("action.download")}
                  onClick={onZipDownloadClick}
                  disabled={filterInfo.dashboardId?.length === 0 || props.ids.length === 0}
                />
              </div>
            </div>
          </div>
        )}
        <DataTable
          className="generated-report-table"
          size="small"
          value={data}
          paginator
          responsiveLayout="scroll"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} - {last} / {totalRecords}`}
          rows={props.itemsPerPage || 10}
          rowsPerPageOptions={rowsPerPageOptions}
          loading={isLoading}
          sortMode="multiple"
        >
          <Column
            field="dashboardTitle"
            header={T("word.report")}
            sortable
            filter
            filterMatchMode="contains"
            filterField="dashboardTitle"
            onFilterApplyClick={filterDashBoardId}
            onFilterClear={() => setFilerInfo(e => ({ ...e, dashboardId: undefined }))}
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            filterPlaceholder="Search"
          />
          {!props.type && (
            <Column
              field="type"
              header={T("word.type")}
              style={{ width: "0" }}
              body={typeBody}
              sortable
              filter
              filterMatchMode="contains"
              filterElement={reportTypesCheckboxFiltering}
              onFilterClear={() => {
                setFilerInfo(e => ({ ...e, onlyType: undefined }));
              }}
              filterField="type"
              showFilterMatchModes={false}
              showFilterMenuOptions={false}
              filterPlaceholder="Search"
            />
          )}

          <Column
            field="status"
            header={T("word.status")}
            style={{ width: "0" }}
            body={statusBody}
          />

          <Column
            field="referenceTime"
            header={T("load-conf.reference-time")}
            body={e =>
              formatDateTimeOrNow(props.referenceTimePattern || "yyyy-MM-dd HH:mm", e.referenceTime)
            }
          />
          <Column
            field="scheduleTime"
            header={T("word.created")}
            body={e => formatTime(e.generationTimeEnd)}
          />
          <Column body={rowData => MarkForRegeneration({ rowData })} style={{ width: "0" }} />
          <Column body={reportHistory} style={{ width: "0" }} />
          <Column body={downloadReportBody} style={{ width: "0" }} />
          <Column body={deleteGenerationBody} style={{ width: "0" }} />
        </DataTable>
      </div>

      <Dialog
        header={T("action.generate")}
        visible={generateDialog}
        style={{ minWidth: "15vw" }}
        onHide={() => setGenerateDialog(false)}
      >
        <GenerateNewReport
          ids={props.ids}
          close={() => setGenerateDialog(false)}
          loadingConfig={props.loadingConfig}
        />
      </Dialog>

      <Dialog
        header={
          dialogFunction === "REMOVE"
            ? T("label.report-table.remove-reports-range")
            : T("label.report-table.cleanup-reports-history")
        }
        visible={removeRangeDialog}
        style={{ minWidth: "15vw" }}
        onHide={() => setRemoveRangeDialog(false)}
      >
        <RemoveReportRange
          ids={props.ids}
          close={() => setRemoveRangeDialog(false)}
          loadingConfig={props.loadingConfig}
          function={dialogFunction}
        />
      </Dialog>
    </>
  );
};

export default GeneratedReportTable;
